import { isStringDirty } from "@suse-o11y/is-string-dirty";
import { FormShape, FormSwitch, FormUnit } from "@suse-o11y/signal/form";
import { z } from "zod";

import {
	type DashboardVariable,
	type DashboardVariableInitConfig,
	type DashboardVariableKind,
	dashboardVariableCommonSchema,
} from "../../common/dashboard-spec";

import type { FormProcessor } from "./_form-processor";
import { ListVariableFormProcessor, type ListVariableFormState } from "./_list-variable";
import { TextVariableFormProcessor, type TextVariableFormState } from "./_text-variable";

export type FormState = FormShape<{
	name: FormUnit<string, ReadonlyArray<string>>;
	label: FormUnit<string, ReadonlyArray<string>, undefined | string>;
	description: FormUnit<string, ReadonlyArray<string>, undefined | string>;

	type: FormSwitch<
		FormUnit<string, ReadonlyArray<string>, DashboardVariableKind>,
		{
			TextVariable: TextVariableFormState;

			ListVariable: ListVariableFormState;
		}
	>;
}>;

const displaySchema = dashboardVariableCommonSchema.shape.display.removeCatch().unwrap();

export const VARIABLE_TYPE_OPTIONS = [
	"TextVariable",
	"ListVariable",
] as const satisfies ReadonlyArray<DashboardVariableKind>;

export const Processor: FormProcessor<FormState, undefined | DashboardVariable, DashboardVariableInitConfig> = {
	fromSpec: (variable) =>
		FormShape({
			name: FormUnit(variable?.name ?? "", {
				schema: dashboardVariableCommonSchema.shape.name,
				isInputDirty: isStringDirty,
			}),
			label: FormUnit(variable?.label ?? "", {
				schema: displaySchema.shape.name,
				isInputDirty: isStringDirty,
			}),
			description: FormUnit(variable?.description ?? "", {
				schema: displaySchema.shape.description,
				isInputDirty: isStringDirty,
			}),

			type: FormSwitch(
				FormUnit(variable?.spec.kind ?? "TextVariable", {
					schema: z.enum(VARIABLE_TYPE_OPTIONS),
				}),
				{
					TextVariable: TextVariableFormProcessor.fromSpec(
						variable?.spec.kind === "TextVariable" ? variable.spec.spec : undefined,
					),

					ListVariable: ListVariableFormProcessor.fromSpec(
						variable?.spec.kind === "ListVariable" ? variable.spec.spec : undefined,
					),
				},
			),
		}),

	toSpec: ({ name, label, description, type }) => ({
		name,
		label,
		description,
		spec:
			type.kind === "TextVariable"
				? { kind: "TextVariable", spec: TextVariableFormProcessor.toSpec(type.value) }
				: { kind: "ListVariable", spec: ListVariableFormProcessor.toSpec(type.value) },
	}),
};
